import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { Container, Row, Col, Stack } from 'react-bootstrap';

import { UniqueWidgetRefreshContext } from 'App';
import BeaufortIcons from 'components/BeaufortIcons/BeaufortIcons';
import ForecastSpread from 'components/ForecastSpread';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import { useAPIData } from 'hooks/useAPIData';
import useDataRefresh from 'hooks/useDataRefresh';
import { DateToString } from 'utils/stringUtils';
import { getDetailedMarine7 } from 'services/weather/marine.service';

const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const DAY_SEGMENTS = ['Morning', 'Afternoon', 'Evening', 'Night'];
const ROUTE = 'getDetailedMarine7';

const spreadStyle = { marginTop: 15, height: '100%', fontSize: 14 };

const buildWaveData = (entry) => ({
  'Wave Height': `${entry.wave_height} ft`,
  'Mean Wave Dir./Period': `${entry.mean_wave_direction_short} / ${entry.mean_wave_period} sec`,
  'Wind Wave Dir./Period': `${entry.wind_wave_direction_short} / ${entry.wind_wave_period} sec`,
  'Peak Wave Dir./Period': `${entry.peak_wave_direction_short} / ${entry.peak_wave_period} sec`,
});

const buildWindData = (entry) => ({
  'Wind Speed & Direction': `${entry.wind_speed} / ${entry.wind_short}`,
  'Wind Gusts': `${entry.wind_gusts}`,
  'Beaufort Description': `${entry.beaufort_desc}`,
  'Sea Surface Temp': `${entry.sea_surface_temp}�F`,
});

export default function WaveAndWind({ tab }) {
  const [data, setData] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const awr = useContext(UniqueWidgetRefreshContext);
  const refreshTokens = useDataRefresh();
  const api = useAPIData();

  const locationKey = `${Cookies.get('city')}-${Cookies.get('state')}`;
  const uniqueKey = `${locationKey}-${ROUTE}`;
  const widgetRefreshToken = awr[uniqueKey];

  const selectedIndex = Number.isInteger(tab) ? tab : new Date().getDay();
  const selectedWeekday = weekdays[selectedIndex];

  const dayEntries = useMemo(
    () => data.filter((entry) => entry.weekday === selectedWeekday),
    [data, selectedWeekday],
  );

  const segmentMap = useMemo(
    () =>
      DAY_SEGMENTS.reduce((acc, segment) => {
        acc[segment] = dayEntries.find((entry) => entry.day_segment === segment) ?? null;
        return acc;
      }, {}),
    [dayEntries],
  );

  const dateLabel = useMemo(() => {
    if (!dayEntries.length) return null;
    const parsed = DateToString(new Date(dayEntries[0].iso8601));
    return `${parsed.month} ${parsed.day}, ${parsed.year} Wave and Wind Conditions`;
  }, [dayEntries]);

  const loadData = useCallback(
    async (force = false) => {
      const hasExistingData = data.length > 0;

      if (!hasExistingData) {
        setIsInitialLoad(true);
      } else {
        setIsRefreshing(true);
      }

      try {
        const requester = force ? api.forceGet : api.get;
        const response = await requester({
          key: ROUTE,
          apiFunction: getDetailedMarine7,
        });
        setData(response?.data?.result ?? []);
      } catch (error) {
        console.error('Error fetching marine forecast:', error);
      } finally {
        setIsInitialLoad(false);
        setIsRefreshing(false);
      }
    },
    [api, data.length],
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadData(true);
  }, [loadData, widgetRefreshToken, refreshTokens.widgetRefresh]);

  const renderSpread = (segment, entry, builder) => {
    if (!entry) return null;
    return (
      <ForecastSpread
        key={segment}
        header={
          <div className="d-flex align-items-center justify-content-between w-100 fw-bold fs-5">
            <div>{segment}</div>
            <div className="me-2">{entry.beaufort_desc}</div>
          </div>
        }
        data={builder(entry)}
        icon={<BeaufortIcons conditionId={entry.beaufort} />}
        style={spreadStyle}
      />
    );
  };

  return (
    <Container fluid className="d-flex flex-column h-100 px-0 user-select-none text-white">
      <WidgetTimeUpdate route={ROUTE} dataUpdateOnly />

      <div className="flex-grow-1 overflow-auto">
        {isInitialLoad ? (
          <div className="h-100 d-flex align-items-center justify-content-center mt-3">
            <WidgetLoadingAnimation />
          </div>
        ) : (
          <Row className="g-4 flex-column flex-lg-row flex-nowrap flex-lg-wrap">
            <Col xs={12} lg={6} className="d-flex">
              <div className="bg-base rounded-3 p-3 w-100 d-flex flex-column position-relative">
                {isRefreshing && (
                  <div className="position-absolute top-0 end-0 me-2 mt-2 small text-secondary">
                    Updating�
                  </div>
                )}
                {dateLabel ? (
                  <div className="border-bottom pb-2 mb-3 fs-5">{dateLabel}</div>
                ) : (
                  <div className="border-bottom pb-2 mb-3 fs-6 text-secondary">
                    No wave forecast data available for {selectedWeekday}.
                  </div>
                )}
                <div className="fs-4 fw-bold">Wave</div>
                <Stack gap={3} className="flex-grow-1">
                  {DAY_SEGMENTS.map((segment) =>
                    renderSpread(segment, segmentMap[segment], buildWaveData),
                  )}
                </Stack>
              </div>
            </Col>

            <Col xs={12} lg={6} className="d-flex">
              <div className="bg-base rounded-3 p-3 w-100 d-flex flex-column position-relative">
                {isRefreshing && (
                  <div className="position-absolute top-0 end-0 me-2 mt-2 small text-secondary">
                    Updating�
                  </div>
                )}
                <div className="fs-4 fw-bold">Wind</div>
                <Stack gap={3} className="flex-grow-1">
                  {DAY_SEGMENTS.map((segment) =>
                    renderSpread(segment, segmentMap[segment], buildWindData),
                  )}
                </Stack>
              </div>
            </Col>
          </Row>
        )}
      </div>
    </Container>
  );
}
